// Host-transfer accept-side manager.
//
// When a relay-paired client sends a hello with `mode: "host-transfer"`,
// `websocket-server.handleHello` hands the connection here instead of through
// `createSessionConnection`. Same trust model as host-exec: the wsToken is
// re-verified locally, and the file access granted is exactly what the
// owner's shell already has — no sandbox, no path allowlist.
//
//   op "get": stream the requested file to the dialer (meta → chunks → done),
//             honouring ack-window flow control.
//   op "put": receive chunks from the dialer into `<path>.appostle-partial`,
//             verify sha256, atomically rename onto `path`, reply `ok`.
//
// Like host-exec, a host-transfer connection does NOT allocate a Session and
// lives in its own map. Unlike host-exec there IS an inbound data leg (acks
// for get, chunks for put) — `websocket-server.handleRawMessage` routes those
// frames to `routeFrame`.

import { createHash } from "node:crypto";
import fs from "node:fs";
import fsp from "node:fs/promises";
import os from "node:os";
import path from "node:path";
import type { Logger } from "pino";

import { verifyWsJwt } from "../ws-jwt.js";
import type { WebSocketLike } from "./host-exec-manager.js";
import {
  HOST_TRANSFER_ACK_EVERY,
  HOST_TRANSFER_CHUNK_BYTES,
  HOST_TRANSFER_IDLE_TIMEOUT_MS,
  HOST_TRANSFER_MAX_DURATION_MS,
  HOST_TRANSFER_MAX_FILE_BYTES,
  HOST_TRANSFER_WINDOW_CHUNKS,
  type HostTransferFrame,
  hostTransferFrameSchema,
} from "./host-transfer-protocol.js";

export interface HostTransferRequest {
  op: "get" | "put";
  path: string;
  /** put only: announced total size, pre-checked against the cap. */
  size?: number;
  /** put only: sha256 (hex) of the incoming file, verified after assembly. */
  sha256?: string;
}

export interface HostTransferAcceptArgs {
  ws: WebSocketLike;
  logger: Logger;
  peerPublicKeyB64: string | null;
  wsToken: string;
  transfer: HostTransferRequest | undefined;
  serverPubkeyHex: string | null;
  serverId: string | null;
}

interface BaseConnection {
  ws: WebSocketLike;
  logger: Logger;
  idleTimer: NodeJS.Timeout | null;
  deadline: NodeJS.Timeout | null;
  finished: boolean;
}

interface GetConnection extends BaseConnection {
  op: "get";
  /** Resolves the sender loop's wait when an ack moves the window. */
  onAck: ((seq: number) => void) | null;
  ackedSeq: number;
}

interface PutConnection extends BaseConnection {
  op: "put";
  filePath: string;
  partialPath: string;
  stream: fs.WriteStream;
  hash: ReturnType<typeof createHash>;
  expectedSize: number;
  expectedSha256: string;
  nextSeq: number;
  bytesWritten: number;
}

type HostTransferConnection = GetConnection | PutConnection;

function expandHome(p: string): string {
  if (p === "~") return os.homedir();
  if (p.startsWith("~/")) return path.join(os.homedir(), p.slice(2));
  return p;
}

export class HostTransferManager {
  private readonly connections = new Map<WebSocketLike, HostTransferConnection>();

  isTransfer(ws: WebSocketLike): boolean {
    return this.connections.has(ws);
  }

  accept(args: HostTransferAcceptArgs): void {
    const { ws, peerPublicKeyB64, wsToken, transfer } = args;
    const log = args.logger.child({ module: "host-transfer" });

    if (!args.serverPubkeyHex) {
      log.warn("host_transfer_rejected_daemon_not_linked");
      this.closeWs(ws, 1008, "auth_required: daemon not linked");
      return;
    }

    const jwt = verifyWsJwt(wsToken, args.serverPubkeyHex, peerPublicKeyB64, args.serverId ?? null);
    if (!jwt.ok) {
      log.warn({ reason: jwt.reason }, "host_transfer_rejected_invalid_jwt");
      this.closeWs(ws, 1008, `auth_required: ${jwt.reason}`);
      return;
    }

    if (!transfer || !transfer.path || (transfer.op !== "get" && transfer.op !== "put")) {
      log.warn("host_transfer_rejected_bad_request");
      this.closeWs(ws, 1008, "bad_transfer_request");
      return;
    }

    const filePath = path.resolve(expandHome(transfer.path));
    log.info({ op: transfer.op, filePath, ownerUserId: jwt.userId }, "host_transfer_accept");

    if (transfer.op === "get") {
      void this.startGet(ws, log, filePath);
    } else {
      void this.startPut(ws, log, filePath, transfer);
    }
  }

  /** Route an inbound frame (ack for get, chunk/done for put) to its connection. */
  routeFrame(ws: WebSocketLike, data: string | Buffer): boolean {
    const conn = this.connections.get(ws);
    if (!conn) return false;
    this.touch(conn);
    let parsed: unknown;
    try {
      parsed = JSON.parse(typeof data === "string" ? data : data.toString("utf8"));
    } catch {
      return true; // ours, but garbage — ignore
    }
    const frame = hostTransferFrameSchema.safeParse(parsed);
    if (!frame.success) return true;

    if (conn.op === "get") {
      if (frame.data.type === "ack") {
        conn.ackedSeq = Math.max(conn.ackedSeq, frame.data.seq);
        conn.onAck?.(conn.ackedSeq);
      } else if (frame.data.type === "ok" || frame.data.type === "error") {
        // Receiver settled; nothing more to do — sender loop closes the WS.
        if (frame.data.type === "error") {
          conn.logger.warn({ message: frame.data.message }, "host_transfer_peer_error");
          this.finish(conn, 1011, "peer_error");
        }
      }
      return true;
    }

    // put: we are the receiver.
    void this.handlePutFrame(conn, frame.data);
    return true;
  }

  /** Called from websocket-server when any WS closes. Returns true if it was ours. */
  handleWsClose(ws: WebSocketLike): boolean {
    const conn = this.connections.get(ws);
    if (!conn) return false;
    this.cleanup(conn);
    return true;
  }

  // ── get: stream a local file to the dialer ───────────────────────────────

  private async startGet(ws: WebSocketLike, log: Logger, filePath: string): Promise<void> {
    let stat: fs.Stats;
    try {
      stat = await fsp.stat(filePath);
    } catch (err) {
      this.sendFrame(ws, { type: "error", message: `stat failed: ${String(err)}` });
      this.closeWs(ws, 1011, "stat_failed");
      return;
    }
    if (!stat.isFile()) {
      this.sendFrame(ws, { type: "error", message: "not a regular file" });
      this.closeWs(ws, 1008, "not_a_file");
      return;
    }
    if (stat.size > HOST_TRANSFER_MAX_FILE_BYTES) {
      this.sendFrame(ws, {
        type: "error",
        message: `file is ${stat.size} bytes; cap is ${HOST_TRANSFER_MAX_FILE_BYTES}`,
      });
      this.closeWs(ws, 1008, "too_large");
      return;
    }

    const conn: GetConnection = {
      op: "get",
      ws,
      logger: log,
      idleTimer: null,
      deadline: null,
      finished: false,
      onAck: null,
      ackedSeq: -1,
    };
    this.connections.set(ws, conn);
    this.arm(conn);

    try {
      // Hash pass first so the receiver can verify the assembled file.
      const sha256 = await this.hashFile(filePath);
      this.sendFrame(ws, { type: "meta", size: stat.size, sha256 });

      const handle = await fsp.open(filePath, "r");
      try {
        const buf = Buffer.alloc(HOST_TRANSFER_CHUNK_BYTES);
        let seq = 0;
        let position = 0;
        for (;;) {
          if (conn.finished) return; // peer errored / closed mid-stream
          const { bytesRead } = await handle.read(buf, 0, buf.length, position);
          if (bytesRead <= 0) break;
          position += bytesRead;
          await this.awaitWindow(conn, seq);
          this.sendFrame(ws, {
            type: "chunk",
            seq,
            dataB64: buf.subarray(0, bytesRead).toString("base64"),
          });
          this.touch(conn);
          seq += 1;
        }
        this.sendFrame(ws, { type: "done", chunks: seq });
        // Leave the connection open: the dialer verifies and closes (or sends
        // `error`). The idle timer reaps it if the dialer vanishes.
      } finally {
        await handle.close();
      }
    } catch (err) {
      log.warn({ err }, "host_transfer_get_failed");
      this.sendFrame(ws, { type: "error", message: String(err) });
      this.finish(conn, 1011, "get_failed");
    }
  }

  /** Block while the unacked window is full. */
  private awaitWindow(conn: GetConnection, nextSeq: number): Promise<void> {
    if (nextSeq - conn.ackedSeq <= HOST_TRANSFER_WINDOW_CHUNKS) return Promise.resolve();
    return new Promise((resolve) => {
      conn.onAck = (acked) => {
        if (nextSeq - acked <= HOST_TRANSFER_WINDOW_CHUNKS || conn.finished) {
          conn.onAck = null;
          resolve();
        }
      };
    });
  }

  // ── put: receive a file from the dialer ──────────────────────────────────

  private async startPut(
    ws: WebSocketLike,
    log: Logger,
    filePath: string,
    transfer: HostTransferRequest,
  ): Promise<void> {
    const size = transfer.size ?? -1;
    const sha256 = transfer.sha256 ?? "";
    if (size < 0 || !sha256) {
      this.sendFrame(ws, { type: "error", message: "put requires size and sha256" });
      this.closeWs(ws, 1008, "bad_put_request");
      return;
    }
    if (size > HOST_TRANSFER_MAX_FILE_BYTES) {
      this.sendFrame(ws, {
        type: "error",
        message: `announced size ${size} exceeds cap ${HOST_TRANSFER_MAX_FILE_BYTES}`,
      });
      this.closeWs(ws, 1008, "too_large");
      return;
    }

    const partialPath = `${filePath}.appostle-partial`;
    let stream: fs.WriteStream;
    try {
      await fsp.access(path.dirname(filePath), fs.constants.W_OK);
      stream = fs.createWriteStream(partialPath, { flags: "w", mode: 0o600 });
      await new Promise<void>((resolve, reject) => {
        stream.once("open", () => resolve());
        stream.once("error", reject);
      });
    } catch (err) {
      this.sendFrame(ws, { type: "error", message: `cannot write destination: ${String(err)}` });
      this.closeWs(ws, 1011, "open_failed");
      return;
    }

    const conn: PutConnection = {
      op: "put",
      ws,
      logger: log,
      idleTimer: null,
      deadline: null,
      finished: false,
      filePath,
      partialPath,
      stream,
      hash: createHash("sha256"),
      expectedSize: size,
      expectedSha256: sha256,
      nextSeq: 0,
      bytesWritten: 0,
    };
    this.connections.set(ws, conn);
    this.arm(conn);
    this.sendFrame(ws, { type: "ready" });
  }

  private async handlePutFrame(conn: PutConnection, frame: HostTransferFrame): Promise<void> {
    if (conn.finished) return;
    if (frame.type === "chunk") {
      if (frame.seq !== conn.nextSeq) {
        this.sendFrame(conn.ws, {
          type: "error",
          message: `out-of-order chunk ${frame.seq}, expected ${conn.nextSeq}`,
        });
        this.finish(conn, 1011, "out_of_order");
        return;
      }
      const bytes = Buffer.from(frame.dataB64, "base64");
      conn.bytesWritten += bytes.byteLength;
      if (conn.bytesWritten > conn.expectedSize) {
        this.sendFrame(conn.ws, { type: "error", message: "more bytes than announced" });
        this.finish(conn, 1011, "overrun");
        return;
      }
      conn.hash.update(bytes);
      conn.nextSeq += 1;
      const canContinue = conn.stream.write(bytes);
      if (!canContinue) {
        await new Promise<void>((resolve) => conn.stream.once("drain", () => resolve()));
      }
      if (conn.nextSeq % HOST_TRANSFER_ACK_EVERY === 0) {
        this.sendFrame(conn.ws, { type: "ack", seq: conn.nextSeq - 1 });
      }
      return;
    }
    if (frame.type === "done") {
      try {
        await new Promise<void>((resolve, reject) => {
          conn.stream.end((err: NodeJS.ErrnoException | null | undefined) =>
            err ? reject(err) : resolve(),
          );
        });
        const digest = conn.hash.digest("hex");
        if (conn.bytesWritten !== conn.expectedSize || digest !== conn.expectedSha256) {
          this.sendFrame(conn.ws, {
            type: "error",
            message: `verification failed (${conn.bytesWritten}/${conn.expectedSize} bytes, sha256 ${digest})`,
          });
          this.finish(conn, 1011, "verify_failed");
          return;
        }
        await fsp.rename(conn.partialPath, conn.filePath);
        conn.logger.info(
          { filePath: conn.filePath, bytes: conn.bytesWritten },
          "host_transfer_put_complete",
        );
        this.sendFrame(conn.ws, { type: "ok", bytes: conn.bytesWritten });
        this.finish(conn, 1000, "host_transfer_done");
      } catch (err) {
        conn.logger.warn({ err }, "host_transfer_put_finalize_failed");
        this.sendFrame(conn.ws, { type: "error", message: String(err) });
        this.finish(conn, 1011, "finalize_failed");
      }
      return;
    }
    if (frame.type === "error") {
      conn.logger.warn({ message: frame.message }, "host_transfer_peer_error");
      this.finish(conn, 1011, "peer_error");
    }
  }

  // ── shared plumbing ───────────────────────────────────────────────────────

  private arm(conn: HostTransferConnection): void {
    this.touch(conn);
    conn.deadline = setTimeout(() => {
      conn.logger.warn("host_transfer_max_duration");
      this.sendFrame(conn.ws, { type: "error", message: "transfer exceeded max duration" });
      this.finish(conn, 1011, "max_duration");
    }, HOST_TRANSFER_MAX_DURATION_MS);
  }

  private touch(conn: HostTransferConnection): void {
    if (conn.idleTimer) clearTimeout(conn.idleTimer);
    conn.idleTimer = setTimeout(() => {
      conn.logger.warn("host_transfer_idle_timeout");
      this.sendFrame(conn.ws, { type: "error", message: "transfer idle timeout" });
      this.finish(conn, 1011, "idle_timeout");
    }, HOST_TRANSFER_IDLE_TIMEOUT_MS);
  }

  private finish(conn: HostTransferConnection, code: number, reason: string): void {
    if (conn.finished) return;
    this.cleanup(conn);
    this.closeWs(conn.ws, code, reason);
  }

  private cleanup(conn: HostTransferConnection): void {
    conn.finished = true;
    if (conn.idleTimer) clearTimeout(conn.idleTimer);
    if (conn.deadline) clearTimeout(conn.deadline);
    conn.idleTimer = null;
    conn.deadline = null;
    if (conn.op === "get") {
      conn.onAck?.(Number.MAX_SAFE_INTEGER); // unblock a parked sender loop
    } else {
      conn.stream.destroy();
      // Best-effort: drop the partial file if the transfer didn't complete.
      void fsp.rm(conn.partialPath, { force: true }).catch(() => {});
    }
    this.connections.delete(conn.ws);
  }

  private async hashFile(filePath: string): Promise<string> {
    const hash = createHash("sha256");
    await new Promise<void>((resolve, reject) => {
      const rs = fs.createReadStream(filePath);
      rs.on("data", (chunk) => hash.update(chunk));
      rs.on("end", () => resolve());
      rs.on("error", reject);
    });
    return hash.digest("hex");
  }

  private sendFrame(ws: WebSocketLike, frame: HostTransferFrame): void {
    try {
      ws.send(JSON.stringify(frame));
    } catch {
      // ignore — channel may already be closing
    }
  }

  private closeWs(ws: WebSocketLike, code: number, reason: string): void {
    try {
      ws.close(code, reason);
    } catch {
      // ignore
    }
  }
}
